import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Warning, Trophy, TrendUp, TrendDown, User, ClipboardText } from "@phosphor-icons/react";

const fmtKg = (n) => `${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })} kg`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

function toneFromPct(pct) {
  if (pct == null) return "neutral";
  if (pct < 2) return "ok";
  if (pct < 5) return "warn";
  return "bad";
}
const toneCls = {
  ok:      "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn:    "bg-amber-50 text-amber-800 border-amber-200",
  bad:     "bg-rose-50 text-rose-800 border-rose-200",
  neutral: "bg-neutral-50 text-neutral-700 border-neutral-200",
};

export default function AuditoriaDesviosCard({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    try {
      const { data } = await api.get("/auditoria-salida/ranking-operadores", {
        params: { desde: fechaDesde, hasta: fechaHasta },
      });
      setData(data);
    } catch (_) {
      setData(null);
    } finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const rows = data?.operadores || [];

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="dash-auditoria-desvios">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
        <div>
          <h2 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <Trophy size={16} weight="bold" className="text-[#6B1F24]" />
            Desvíos de Peso por Operador
          </h2>
          <div className="text-[11px] text-[#666] font-mono mt-0.5">
            % de ítems auditados con diferencia real vs producido · rango seleccionado
          </div>
        </div>
        <div className="text-[11px] text-[#666] font-mono uppercase tracking-widest">
          {loading ? "Cargando…" : `${rows.length} operadores`}
        </div>
      </div>
      {!loading && rows.length === 0 && (
        <div className="p-8 text-center text-[#999] text-[13px]">
          <ClipboardText size={30} weight="duotone" className="mx-auto mb-2 text-neutral-300" />
          Sin auditorías en el período seleccionado.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
              <tr>
                <th className="px-4 py-2 text-left w-8">#</th>
                <th className="px-4 py-2 text-left">Operador</th>
                <th className="px-4 py-2 text-right w-24">Producidos</th>
                <th className="px-4 py-2 text-right w-24">Auditados</th>
                <th className="px-4 py-2 text-right w-24">Con dif.</th>
                <th className="px-4 py-2 text-right w-20">% Desvío</th>
                <th className="px-4 py-2 text-right w-28">Δ kg promedio</th>
                <th className="px-4 py-2 text-left w-24">Sesgo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const t = toneFromPct(r.pct_desvio);
                return (
                  <tr key={r.username || i} className="border-t border-neutral-100 hover:bg-neutral-50"
                      data-testid={`dash-aud-desv-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-neutral-500">{i + 1}</td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <User size={12} weight="bold" className="text-[#6B1F24]" />
                        <span className="font-medium">{r.username || "—"}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{r.items_producidos}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.items_auditados}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.items_con_diferencia}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={`inline-block px-2 py-0.5 rounded-sm border font-mono font-bold text-[12px] ${toneCls[t]}`}>
                        {r.pct_desvio != null ? fmtPct(r.pct_desvio) : "—"}
                      </span>
                    </td>
                    <td className={`px-4 py-2 text-right font-mono ${r.delta_kg_prom > 0 ? "text-emerald-700" : r.delta_kg_prom < 0 ? "text-rose-700" : "text-neutral-500"}`}>
                      {r.delta_kg_prom != null && Math.abs(r.delta_kg_prom) > 0.001
                        ? `${r.delta_kg_prom > 0 ? "+" : ""}${fmtKg(r.delta_kg_prom)}`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-[11px]">
                      {r.sesgo === "de_mas" && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-mono">
                          <TrendUp size={11} weight="bold" /> Carga de más
                        </span>
                      )}
                      {r.sesgo === "de_menos" && (
                        <span className="inline-flex items-center gap-1 text-rose-700 font-mono">
                          <TrendDown size={11} weight="bold" /> Carga de menos
                        </span>
                      )}
                      {(!r.sesgo || r.sesgo === "balanceado") && (
                        <span className="text-neutral-500 font-mono italic text-[10px]">balanceado</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50/50 text-[10px] font-mono text-neutral-500 flex items-center gap-2">
            <Warning size={11} className="text-amber-600" />
            <span>
              Semáforo: <span className="text-emerald-700 font-bold">verde &lt;2%</span> ·{" "}
              <span className="text-amber-700 font-bold">amarillo 2–5%</span> ·{" "}
              <span className="text-rose-700 font-bold">rojo &gt;5%</span>. Se cuentan sólo ítems auditados con diferencia real (kg producidos ≠ peso real).
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
